import { useState, useEffect } from 'react';
import axios from 'axios';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import { CertificatePanel } from './TrainingCertificates';
import { AddRegistrationModal, AddParticipantModal } from './TrainingRegistrationForms';

const STAGES = [
  { key: 'registered',       label: 'Registered',        color: 'bg-gray-50 text-gray-500 border-gray-200' },
  { key: 'grant_guide_sent', label: 'Guide Sent',         color: 'bg-blue-50 text-blue-600 border-blue-200' },
  { key: 'grant_applied',    label: 'Grant Applied',      color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  { key: 'grant_approved',   label: 'Grant Approved',     color: 'bg-green-50 text-green-700 border-green-200' },
  { key: 'jd14_collected',   label: 'JD14 Collected',     color: 'bg-purple-50 text-purple-600 border-purple-200' },
  { key: 'training_attended',label: 'Attended',           color: 'bg-carriera-accent/10 text-carriera-accent border-carriera-accent/30' },
  { key: 'certificates_sent',label: 'Certs Sent',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'claim_submitted',  label: 'Claim Submitted',    color: 'bg-slate-50 text-slate-600 border-slate-200' },
];

const emptyForm = {
  title: '', date: '', end_date: '', time: '', end_time: '', venue: '',
  price: '', cpd_hours: '', hrd_corp_claimable: true, in_house: false,
  speaker_name: '', description: '', pdf_url: '', status: 'upcoming'
};

const STAGE_LABELS = {
  registered: 'Registered', grant_guide_sent: 'Guide Sent',
  grant_applied: 'Applied', grant_approved: 'Approved',
  jd14_collected: 'JD14', training_attended: 'Attended',
  certificates_sent: 'Certs Sent', claim_submitted: 'Claimed'
};

function RegistrationSummary({ regs }) {
  if (!regs || regs.length === 0) return (
    <p className="text-xs text-carriera-muted mt-1" style={{ opacity: 0.5 }}>No registrations yet</p>
  );
  const counts = regs.reduce((acc, r) => { acc[r.stage] = (acc[r.stage] || 0) + 1; return acc; }, {});
  return (
    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
      <span className="text-xs font-medium text-carriera-text">{regs.length} registration{regs.length !== 1 ? 's' : ''}</span>
      <span className="text-carriera-border text-xs">·</span>
      {Object.entries(counts).map(([stage, count]) => (
        <span key={stage} className="text-xs text-carriera-muted">{count} {STAGE_LABELS[stage] || stage}</span>
      ))}
    </div>
  );
}

function StageBadge({ stage }) {
  const s = STAGES.find(s => s.key === stage) || STAGES[0];
  return <span className={`text-xs font-medium px-2 py-0.5 border rounded-full ${s.color}`}>{s.label}</span>;
}

export default function Training() {
  const [trainings, setTrainings]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs, setLoadingRegs]   = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [editMode, setEditMode]         = useState(false);
  const [form, setForm]                 = useState(emptyForm);
  const [submitting, setSubmitting]     = useState(false);
  const [selectedReg, setSelectedReg]   = useState(null);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [activeTab, setActiveTab]       = useState('upcoming');
  const [lapseDismissed, setLapseDismissed] = useState(false);
  const [addingReg, setAddingReg]       = useState(false);
  const [addingTo, setAddingTo]         = useState(null);   // registration getting one more participant

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const { markClean, confirmClose } = useUnsavedChanges();
  const inp     = "w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200";
  const lbl     = "block text-xs text-carriera-muted uppercase tracking-widest mb-1";

  useEffect(() => { fetchTrainings(); }, []);

  async function fetchTrainings() {
    try {
      const res = await axios.get('https://carriera-backend.onrender.com/api/trainings', { headers });
      setTrainings(res.data);
    } catch { } finally { setLoading(false); }
  }

  async function fetchRegistrations(trainingId) {
    setLoadingRegs(true);
    try {
      const res = await axios.get(`https://carriera-backend.onrender.com/api/trainings/${trainingId}/registrations`, { headers });
      setRegistrations(res.data);
    } catch { } finally { setLoadingRegs(false); }
  }

  function selectTraining(t) {
    setSelected(t);
    setSelectedReg(null);
    setLapseDismissed(false);
    fetchRegistrations(t.id);
  }

  async function quickUpdateStatus(status) {
    try {
      const res = await axios.patch(`https://carriera-backend.onrender.com/api/trainings/${selected.id}`, { status }, { headers });
      setTrainings(prev => prev.map(t => t.id === selected.id ? res.data : t));
      setSelected(res.data);
      setLapseDismissed(true);
    } catch { alert('Failed to update status.'); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editMode && selected) {
        const res = await axios.patch(`https://carriera-backend.onrender.com/api/trainings/${selected.id}`, form, { headers });
        setTrainings(prev => prev.map(t => t.id === selected.id ? res.data : t));
        setSelected(res.data);
      } else {
        const res = await axios.post('https://carriera-backend.onrender.com/api/trainings', form, { headers });
        setTrainings(prev => [res.data, ...prev]);
      }
      setShowForm(false);
      setEditMode(false);
      setForm(emptyForm);
    } catch (err) { alert('Failed to save: ' + (err.response?.data?.error || err.message || 'Unknown error')); }
    finally { setSubmitting(false); }
  }

  async function updateStage(regId, stage) {
    setUpdatingStage(true);
    try {
      const res = await axios.patch(`https://carriera-backend.onrender.com/api/trainings/registrations/${regId}/stage`, { stage }, { headers });
      // The stage update doesn't return certificates, so keep the ones we already have.
      const old = registrations.find(r => r.id === regId);
      const certOf = id => old?.training_participants?.find(p => p.id === id)?.certificate || null;
      const updated = { ...res.data, training_participants: (res.data.training_participants || []).map(p => ({ ...p, certificate: certOf(p.id) })) };
      setRegistrations(prev => prev.map(r => r.id === regId ? updated : r));
      if (selectedReg?.id === regId) setSelectedReg(updated);
    } catch { alert('Failed to update stage.'); }
    finally { setUpdatingStage(false); }
  }

  const upcoming  = trainings.filter(t => t.status === 'upcoming' || t.status === 'ongoing');
  const completed = trainings.filter(t => t.status === 'completed');
  const cancelled = trainings.filter(t => t.status === 'cancelled');

  const TABS = [
    { key: 'upcoming',  label: 'Upcoming',  list: upcoming },
    { key: 'completed', label: 'Completed', list: completed },
    { key: 'cancelled', label: 'Cancelled', list: cancelled },
  ];
  const activeList = TABS.find(t => t.key === activeTab)?.list || [];

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-carriera-muted text-sm">Loading...</p></div>;

  // ── FULL PAGE DETAIL VIEW ──────────────────────────
  if (selected && !showForm) {
    const today = new Date().toISOString().slice(0, 10);
    const lapseDate = selected.end_date || selected.date;
    const isLapsed = (selected.status === 'upcoming' || selected.status === 'ongoing') && lapseDate && lapseDate < today;

  return (
    <div className="h-full flex flex-col">
      {/* Lapsed reminder */}
      {isLapsed && !lapseDismissed && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm font-medium text-yellow-800">This programme's date has passed but it's still marked "{selected.status === 'ongoing' ? 'Ongoing' : 'Upcoming'}".</p>
            <p className="text-xs text-yellow-700 mt-0.5">Update its status so it's reflected correctly in the dashboard.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => quickUpdateStatus('completed')}
              className="text-xs font-medium px-3 py-1.5 bg-carriera-text text-white hover:bg-carriera-accent transition-colors duration-200">
              Mark Completed
            </button>
            <button onClick={() => quickUpdateStatus('cancelled')}
              className="text-xs font-medium px-3 py-1.5 border border-carriera-border text-carriera-sec hover:border-carriera-accent transition-colors duration-200">
              Mark Cancelled
            </button>
            <button onClick={() => setLapseDismissed(true)}
              className="text-xs text-carriera-muted hover:text-carriera-text transition-colors duration-200">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <button onClick={() => { setSelected(null); setSelectedReg(null); }}
            className="text-xs text-carriera-muted hover:text-carriera-accent transition-colors duration-200 mb-2 flex items-center gap-1">
            ← Back to Programmes
          </button>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">{selected.title}</h2>
          {selected.in_house && (
            <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-700 border border-slate-300 px-2 py-0.5 rounded-full">In-house · internal only, not on the website</span>
          )}
          <p className="text-sm text-carriera-muted mt-1">
            {selected.date ? new Date(selected.date + 'T00:00:00').toLocaleDateString('en-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date TBD'}
            {selected.end_date && selected.end_date !== selected.date
              ? ` – ${new Date(selected.end_date + 'T00:00:00').toLocaleDateString('en-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(selected.status === 'upcoming' || selected.status === 'ongoing') && (
            <>
              <button onClick={() => quickUpdateStatus('completed')}
                className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-carriera-accent hover:text-carriera-accent transition-colors duration-200">
                Mark Completed
              </button>
              <button onClick={() => quickUpdateStatus('cancelled')}
                className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-red-300 hover:text-red-500 transition-colors duration-200">
                Mark Cancelled
              </button>
            </>
          )}
          {(selected.status === 'completed' || selected.status === 'cancelled') && (
            <button onClick={() => quickUpdateStatus('upcoming')}
              className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-carriera-accent hover:text-carriera-accent transition-colors duration-200">
              Mark Upcoming
            </button>
          )}
          <button onClick={async () => {
            if (!window.confirm(`Delete "${selected.title}"? This cannot be undone.`)) return;
            try {
              await axios.delete(`https://carriera-backend.onrender.com/api/trainings/${selected.id}`, { headers });
              setTrainings(prev => prev.filter(t => t.id !== selected.id));
              setSelected(null);
            } catch { alert('Failed to delete programme.'); }
          }} className="text-sm text-red-400 hover:text-red-600 transition-colors duration-200">
            Delete
          </button>
          <button onClick={() => {
            const next = { ...selected, price: selected.price || '', cpd_hours: selected.cpd_hours || '', end_date: selected.end_date || '' };
            setForm(next); setEditMode(true); setShowForm(true); markClean(next);
          }}
            className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent transition-colors duration-200">
            Edit Programme
          </button>
        </div>
      </div>

      {/* Programme details grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Venue',     value: selected.venue || '—' },
          { label: 'Time',      value: selected.time ? `${selected.time.slice(0,5)}${selected.end_time ? ` – ${selected.end_time.slice(0,5)}` : ''}` : '—' },
          { label: 'Price',     value: selected.price ? `MYR ${Number(selected.price).toLocaleString()} / pax` : '—' },
          { label: 'CPD Hours', value: selected.cpd_hours ? `${selected.cpd_hours} hours` : '—' },
          { label: 'Speaker',   value: selected.speaker_name || '—' },
          { label: 'HRD Corp',  value: selected.hrd_corp_claimable ? 'Claimable ✓' : 'Not claimable' },
        ].map(item => (
          <div key={item.label} className="bg-white border border-carriera-border p-4">
            <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">{item.label}</p>
            <p className="text-sm text-carriera-text font-medium">{item.value}</p>
          </div>
        ))}
      </div>

      {selected.pdf_url && (
        <div className="mb-6">
          <a href={selected.pdf_url} target="_blank" rel="noreferrer"
            className="text-sm text-carriera-accent underline">View Programme PDF →</a>
        </div>
      )}

      {!loadingRegs && <CertificatePanel registrations={registrations} setRegistrations={setRegistrations} headers={headers} />}

      {/* Registrations */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-carriera-muted uppercase tracking-widest">
          Registrations ({registrations.length})
        </p>
        <button onClick={() => setAddingReg(true)}
          className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-carriera-accent hover:text-carriera-accent transition-colors duration-200">
          + Add registration
        </button>
      </div>
      {addingReg && (
        <AddRegistrationModal training={selected} headers={headers} onClose={() => setAddingReg(false)}
          onSaved={() => { setAddingReg(false); fetchRegistrations(selected.id); fetchTrainings(); }} />
      )}
      {addingTo && (
        <AddParticipantModal registration={addingTo} headers={headers} onClose={() => setAddingTo(null)}
          onSaved={() => { setAddingTo(null); fetchRegistrations(selected.id); }} />
      )}

      <div className="flex-1 overflow-y-auto">
        {loadingRegs ? (
          <p className="text-sm text-carriera-muted">Loading registrations...</p>
        ) : registrations.length === 0 ? (
          <div className="bg-white border border-carriera-border p-8 text-center">
            <p className="text-sm text-carriera-muted italic">No registrations yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {registrations.map(reg => (
              <div key={reg.id} className="bg-white border border-carriera-border">
                <div className="p-5 cursor-pointer hover:bg-carriera-bg transition-colors duration-200"
                  onClick={() => setSelectedReg(selectedReg?.id === reg.id ? null : reg)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-carriera-text">{reg.company_name}</p>
                      <p className="text-xs text-carriera-muted mt-0.5">
                        {reg.pic_name}{reg.pic_job_title ? `, ${reg.pic_job_title}` : ''} &nbsp;·&nbsp;
                        {reg.training_participants?.length || 0} participant{reg.training_participants?.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                        Registered {new Date(reg.created_at).toLocaleDateString('en-MY')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StageBadge stage={reg.stage} />
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete registration for ${reg.company_name}? This cannot be undone.`)) return;
                        try {
                          await axios.delete(`https://carriera-backend.onrender.com/api/trainings/registrations/${reg.id}`, { headers });
                          setRegistrations(prev => prev.filter(r => r.id !== reg.id));
                          if (selectedReg?.id === reg.id) setSelectedReg(null);
                        } catch { alert('Failed to delete registration.'); }
                      }} className="text-xs text-red-400 hover:text-red-600 transition-colors duration-200">
                        Delete
                      </button>
                      <span className="text-carriera-muted text-xs">{selectedReg?.id === reg.id ? '▲' : '▼'}</span>
                    </div>
                  </div>
                </div>

                {selectedReg?.id === reg.id && (
                  <div className="border-t border-carriera-border p-5 bg-carriera-bg space-y-5">

                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: 'PIC',       value: `${reg.pic_name}${reg.pic_job_title ? `, ${reg.pic_job_title}` : ''}` },
                        { label: 'Phone',     value: reg.pic_phone || '—' },
                        { label: 'Email',     value: reg.pic_email },
                        { label: 'Address',   value: reg.company_address || '—' },
                        { label: 'Payment',   value: reg.payment_method || '—' },
                        { label: 'How Found', value: reg.how_found || '—' },
                      ].map(item => (
                        <div key={item.label}>
                          <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                          <p className="text-sm text-carriera-text">{item.value}</p>
                        </div>
                      ))}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-carriera-muted uppercase tracking-widest">Participants</p>
                          <button onClick={() => setAddingTo(reg)} className="text-xs text-carriera-accent hover:underline">+ Add participant</button>
                        </div>
                        {!reg.training_participants?.length && <p className="text-xs text-carriera-muted italic">None yet.</p>}
                        <div className="grid grid-cols-2 gap-2">
                          {(reg.training_participants || []).map((p, i) => (
                            <div key={p.id} className="bg-white border border-carriera-border p-3">
                              <p className="text-sm font-medium text-carriera-text">{i + 1}. {p.full_name}</p>
                              <p className="text-xs text-carriera-muted mt-0.5">{p.job_title || '—'} · {p.email || '—'} · {p.phone || '—'}</p>
                              {p.zoom_link && <p className="text-xs text-carriera-accent mt-1 truncate">Zoom: {p.zoom_link}</p>}
                            </div>
                          ))}
                        </div>
                    </div>

                    <div>
                      <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Update Stage</p>
                      <div className="grid grid-cols-4 gap-2">
                        {STAGES.map(s => (
                          <button key={s.key} disabled={updatingStage || reg.stage === s.key}
                            onClick={() => updateStage(reg.id, s.key)}
                            className={`text-xs py-2 px-2 border transition-colors duration-200 text-center ${
                              reg.stage === s.key
                                ? 'bg-carriera-accent text-white border-carriera-accent'
                                : 'bg-white text-carriera-sec border-carriera-border hover:border-carriera-accent'
                            }`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {reg.notes && (
                      <div>
                        <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Notes</p>
                        <p className="text-sm text-carriera-text whitespace-pre-line">{reg.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  }

  return (
    <div className="h-full flex flex-col">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Academy</p>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">Training Programmes</h2>
        </div>
        <button onClick={() => { setShowForm(true); setEditMode(false); setForm(emptyForm); markClean(emptyForm); }}
          className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent transition-colors duration-200">
          + New Programme
        </button>
      </div>

      {/* Status tabs */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`text-left bg-white border p-4 transition-colors duration-200 ${activeTab === t.key ? 'border-carriera-accent' : 'border-carriera-border hover:border-carriera-accent/50'}`}
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">{t.label}</p>
            <p className="font-serif text-2xl font-normal text-carriera-text">{t.list.length}</p>
          </button>
        ))}
      </div>

      {/* Training list */}
      <div className="bg-white border border-carriera-border flex-1 overflow-y-auto">
        {activeList.length === 0 ? (
          <div className="p-8 text-center"><p className="text-carriera-muted text-sm">No {activeTab} programmes.</p></div>
        ) : (
          activeList.map((t, i) => (
            <div key={t.id} onClick={() => selectTraining(t)}
              className={`flex items-center justify-between p-4 cursor-pointer hover:bg-carriera-bg transition-colors duration-200 ${i > 0 ? 'border-t border-carriera-border' : ''} ${selected?.id === t.id ? 'bg-carriera-bg' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-carriera-text">{t.title}</p>
                <p className="text-xs text-carriera-muted mt-0.5">
                  {t.date ? new Date(t.date + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD'}
                  {t.end_date && t.end_date !== t.date ? ` – ${new Date(t.end_date + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  {t.venue ? ` · ${t.venue}` : ''}
                  {t.cpd_hours ? ` · ${t.cpd_hours} CPD hrs` : ''}
                  {t.price ? ` · MYR ${Number(t.price).toLocaleString()}` : ''}
                </p>
                <RegistrationSummary regs={t.training_registrations || []} />
              </div>
              <div className="flex items-center gap-2 ml-4">
                {t.in_house && (
                  <span className="text-xs bg-slate-100 text-slate-700 border border-slate-300 px-2 py-0.5 rounded-full">In-house</span>
                )}
                {t.hrd_corp_claimable && (
                  <span className="text-xs bg-carriera-accent/10 text-carriera-accent border border-carriera-accent/30 px-2 py-0.5 rounded-full">HRD Corp</span>
                )}
                <span className={`text-xs px-2 py-0.5 border rounded-full ${t.status === 'upcoming' ? 'bg-green-50 text-green-700 border-green-200' : t.status === 'completed' ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-red-50 text-red-500 border-red-200'}`}>
                  {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── TRAINING DETAIL PANEL (unused - kept for reference) ── */}
      {false && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end">
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">

            <div className="p-6 border-b border-carriera-border flex items-start justify-between">
              <div>
                <h3 className="font-serif text-xl font-normal text-carriera-text">{selected.title}</h3>
                <p className="text-sm text-carriera-muted mt-0.5">
                  {selected.date ? new Date(selected.date + 'T00:00:00').toLocaleDateString('en-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date TBD'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { setForm({ ...selected, price: selected.price || '', cpd_hours: selected.cpd_hours || '', end_date: selected.end_date || '' }); setEditMode(true); setShowForm(true); }}
                  className="text-xs text-carriera-muted hover:text-carriera-accent transition-colors duration-200">Edit</button>
                <button onClick={() => { setSelected(null); setSelectedReg(null); }} className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
              </div>
            </div>

            <div className="p-6">

              {/* Programme details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Venue',    value: selected.venue || '—' },
                  { label: 'Time',     value: selected.time ? `${selected.time}${selected.end_time ? ` – ${selected.end_time}` : ''}` : '—' },
                  { label: 'Price',    value: selected.price ? `MYR ${Number(selected.price).toLocaleString()}` : '—' },
                  { label: 'CPD Hours',value: selected.cpd_hours ? `${selected.cpd_hours} hours` : '—' },
                  { label: 'Speaker',  value: selected.speaker_name || '—' },
                  { label: 'HRD Corp', value: selected.hrd_corp_claimable ? 'Claimable ✓' : 'Not claimable' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                    <p className="text-sm text-carriera-text">{item.value}</p>
                  </div>
                ))}
              </div>

              {selected.pdf_url && (
                <div className="mb-6">
                  <a href={selected.pdf_url} target="_blank" rel="noreferrer"
                    className="text-sm text-carriera-accent underline">View Programme PDF →</a>
                </div>
              )}

              <hr className="border-carriera-border mb-6" />

              {/* Registrations */}
              <p className="text-xs text-carriera-muted uppercase tracking-widest mb-3">
                Registrations ({registrations.length})
              </p>

              {loadingRegs ? (
                <p className="text-sm text-carriera-muted">Loading registrations...</p>
              ) : registrations.length === 0 ? (
                <p className="text-sm text-carriera-muted italic">No registrations yet.</p>
              ) : (
                <div className="space-y-3">
                  {registrations.map(reg => (
                    <div key={reg.id} className="border border-carriera-border">
                      {/* Registration header */}
                      <div className="p-4 cursor-pointer hover:bg-carriera-bg transition-colors duration-200"
                        onClick={() => setSelectedReg(selectedReg?.id === reg.id ? null : reg)}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-carriera-text">{reg.company_name}</p>
                            <p className="text-xs text-carriera-muted mt-0.5">
                              {reg.pic_name} · {reg.training_participants?.length || 0} participant{reg.training_participants?.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <StageBadge stage={reg.stage} />
                        </div>
                      </div>

                      {/* Expanded registration details */}
                      {selectedReg?.id === reg.id && (
                        <div className="border-t border-carriera-border p-4 bg-carriera-bg space-y-4">

                          {/* PIC details */}
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { label: 'PIC',      value: `${reg.pic_name}${reg.pic_job_title ? `, ${reg.pic_job_title}` : ''}` },
                              { label: 'Phone',    value: reg.pic_phone || '—' },
                              { label: 'Email',    value: reg.pic_email },
                              { label: 'Address',  value: reg.company_address || '—' },
                              { label: 'Payment',  value: reg.payment_method || '—' },
                              { label: 'How Found',value: reg.how_found || '—' },
                              { label: 'Registered', value: new Date(reg.created_at).toLocaleDateString('en-MY') },
                            ].map(item => (
                              <div key={item.label}>
                                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                                <p className="text-xs text-carriera-text">{item.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Participants */}
                          {reg.training_participants?.length > 0 && (
                            <div>
                              <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Participants</p>
                              <div className="space-y-2">
                                {reg.training_participants.map((p, i) => (
                                  <div key={p.id} className="bg-white border border-carriera-border p-3">
                                    <p className="text-xs font-medium text-carriera-text">{i + 1}. {p.full_name}</p>
                                    <p className="text-xs text-carriera-muted">{p.job_title || '—'} · {p.email || '—'} · {p.phone || '—'}</p>
                                    {p.zoom_link && <p className="text-xs text-carriera-accent mt-1 truncate">Zoom: {p.zoom_link}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Stage update */}
                          <div>
                            <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Update Stage</p>
                            <div className="grid grid-cols-2 gap-2">
                              {STAGES.map(s => (
                                <button key={s.key} disabled={updatingStage || reg.stage === s.key}
                                  onClick={() => updateStage(reg.id, s.key)}
                                  className={`text-xs py-1.5 px-2 border transition-colors duration-200 text-left ${
                                    reg.stage === s.key
                                      ? 'bg-carriera-accent text-white border-carriera-accent'
                                      : 'bg-white text-carriera-sec border-carriera-border hover:border-carriera-accent'
                                  }`}>
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {reg.notes && (
                            <div>
                              <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Notes</p>
                              <p className="text-xs text-carriera-text whitespace-pre-line">{reg.notes}</p>
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT FORM ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end"
          onClick={() => confirmClose(form, () => { setShowForm(false); setEditMode(false); })}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="p-6 border-b border-carriera-border flex items-center justify-between">
              <h3 className="font-serif text-xl font-normal text-carriera-text">
                {editMode ? 'Edit Programme' : 'New Training Programme'}
              </h3>
              <button onClick={() => confirmClose(form, () => { setShowForm(false); setEditMode(false); })} className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">

              <div>
                <label className={lbl}>Programme Title <span className="text-red-500">*</span></label>
                <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Employment Act & Labour Law Compliance" className={inp} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Start Date</label>
                  <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className={inp} />
                </div>
                <div>
                  <label className={lbl}>End Date <span className="text-carriera-muted font-normal">(if multi-day)</span></label>
                  <input type="date" value={form.end_date || ''} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} className={inp} />
                </div>
              </div>

              <div>
                <label className={lbl}>Venue / Mode</label>
                <input type="text" value={form.venue} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))}
                  placeholder="e.g. Shah Alam / Online – Zoom" className={inp} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Start Time</label>
                  <input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className={inp} />
                </div>
                <div>
                  <label className={lbl}>End Time</label>
                  <input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} className={inp} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Price (MYR)</label>
                  <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="e.g. 800" className={inp} />
                </div>
                <div>
                  <label className={lbl}>CPD Hours</label>
                  <input type="number" value={form.cpd_hours} onChange={e => setForm(p => ({ ...p, cpd_hours: e.target.value }))}
                    placeholder="e.g. 8" className={inp} />
                </div>
              </div>

              <div>
                <label className={lbl}>Speaker / Trainer Name</label>
                <input type="text" value={form.speaker_name} onChange={e => setForm(p => ({ ...p, speaker_name: e.target.value }))}
                  placeholder="e.g. Dato' Ahmad Razali" className={inp} />
              </div>

              <div>
                <label className={lbl}>Programme PDF URL</label>
                <input type="url" value={form.pdf_url} onChange={e => setForm(p => ({ ...p, pdf_url: e.target.value }))}
                  placeholder="Google Drive or Dropbox shareable link" className={inp} />
                <p className="text-xs text-carriera-muted mt-1">Upload PDF to Google Drive → Share → Copy link → Paste here</p>
              </div>

              <div>
                <label className={lbl}>Course Details <span className="text-carriera-muted font-normal">(website page content)</span></label>
                <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={6} placeholder="Leave blank to auto-read the brochure PDF above when you save. You can also paste or tidy the text here." className={inp} />
                <p className="text-xs text-carriera-muted mt-1">This becomes the searchable content on the public course page. Blank + a brochure link = auto-filled on save.</p>
              </div>

              <div className="flex items-center gap-3">
                <input type="checkbox" id="hrd" checked={form.hrd_corp_claimable}
                  onChange={e => setForm(p => ({ ...p, hrd_corp_claimable: e.target.checked }))}
                  className="w-4 h-4 accent-carriera-accent" />
                <label htmlFor="hrd" className="text-sm text-carriera-text">HRD Corp Claimable</label>
              </div>

              <div className="flex items-start gap-3">
                <input type="checkbox" id="in-house" checked={!!form.in_house}
                  onChange={e => setForm(p => ({ ...p, in_house: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 accent-carriera-accent" />
                <label htmlFor="in-house" className="text-sm text-carriera-text">
                  In-house (internal only)
                  <span className="block text-xs text-carriera-muted">Not shown on the website or Google, and the public registration form refuses it. Add participants yourself with "+ Add registration".</span>
                </label>
              </div>

              <div>
                <label className={lbl}>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className={inp}>
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <button type="submit" disabled={submitting}
                className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200 disabled:opacity-50">
                {submitting ? 'Saving...' : editMode ? 'Save Changes' : 'Create Programme'}
              </button>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}
